import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../../../components/Navbar";
import { 
  ChevronRight, 
  ArrowLeft, 
  Plus,
  Settings,
  Search,
  MoreVertical,
  Eye,
  Edit2,
  Trash2,
  Users,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle
} from "lucide-react";
import { useCustomRoles } from "../../../hooks/permissionHooks/useCustomRoles";
import { useTeamValidation } from "../../../hooks/permissionHooks/useTeamValidation";
import { useTeamManagement } from "../../../hooks/permissionHooks/useTeamManagement";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import { usePermissionCheck } from "../../../hooks/permissionHooks/usePermissionCheck";

const ActionDropdown = ({
  teamMemberCount,
  role, 
  activeDropdown, 
  setActiveDropdown,
  onView,
  onEdit,
  onDelete 
}) => {
  const dropdownRef = useRef(null);
  const { canDeleteRole } = useTeamValidation();
  const { canEditRoles, canDeleteRoles } = usePermissionCheck();

  const { canDelete, reason, affectedMembers } = canDeleteRole(role.id || role.role_slug);

  // Create a more robust unique identifier for this role
  const roleIdentifier = role.id || role.role_slug || `${role.name}_${Math.random().toString(36).substr(2, 9)}`;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    if (activeDropdown === roleIdentifier) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdown, roleIdentifier, setActiveDropdown]);

  const actions = [
    {
      icon: <Eye className="w-4 h-4" />,
      label: "View Details",
      color: "text-blue-600",
      onClick: () => onView(role),
    }
  ];

  // Only add edit action if user has permission
  if (canEditRoles()) {
    actions.push({
      icon: <Edit2 className="w-4 h-4" />,
      label: "Edit Role",
      color: "text-green-600",
      onClick: () => onEdit(role),
    });
  }

  // Debug logging
  console.log(`Role: ${role.name}`, {
    canDeleteRoles: canDeleteRoles(),
    canDelete: canDelete,
    teamMemberCount: teamMemberCount,
    roleId: role.id,
    roleSlug: role.role_slug,
    reason: reason,
    fullRole: role,
    affectedMembers: affectedMembers
  });

  // Only add delete action if user has permission and role can be deleted
  if (canDeleteRoles() && canDelete) {
    actions.push({
      icon: <Trash2 className="w-4 h-4" />,
      label: "Delete Role",
      color: "text-red-600",
      onClick: () => onDelete(role),
    });
  }

  return (
    <div className="relative" ref={dropdownRef} data-role-id={roleIdentifier}>
      <motion.button
        id={`action-button-${roleIdentifier}`}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={(e) => {
          e.stopPropagation();
           setActiveDropdown(activeDropdown === roleIdentifier ? null : roleIdentifier);
        }}
        className="p-2 rounded-lg transition-colors duration-200 hover:bg-gray-100"
      >
        <MoreVertical className="w-5 h-5 text-gray-600" />
      </motion.button>

      <AnimatePresence>
        {activeDropdown === roleIdentifier && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="absolute right-0 top-full z-50 py-2 mt-2 w-56 bg-white rounded-xl border border-gray-100 shadow-xl origin-top-right"
          >
            {actions.map((action) => (
              <motion.button
                key={action.label}
                whileHover={action.disabled ? {} : { x: 4 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveDropdown(null);
                  if (action.onClick && !action.disabled) {
                    action.onClick();
                  }
                }}
                disabled={action.disabled}
                title={action.tooltip}
                className={`w-full px-4 py-2 flex items-center space-x-3 hover:bg-gray-50 ${action.color} ${
                  action.disabled ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                {action.icon}
                <span className="text-sm font-medium">{action.label}</span>
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const RoleCard = ({ 
  role, 
  activeDropdown, 
  setActiveDropdown,
  onView,
  onEdit,
  onDelete,
  teamMemberCount 
}) => {
  const totalPermissions = Object.values(role.permissions || {}).flat().length;
  const moduleCount = Object.keys(role.permissions || {}).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className="overflow-hidden relative bg-white rounded-2xl border border-gray-100 shadow-sm backdrop-blur-sm transition-all duration-300 group hover:shadow-xl hover:border-gray-200"
    >
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#FF6C6C]/5 via-transparent to-pink-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02] group-hover:opacity-[0.05] transition-opacity duration-300">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #FF6C6C 1px, transparent 0)`,
          backgroundSize: '20px 20px'
        }} />
      </div>
      
      <div className="relative p-4">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex gap-3 items-center">
            <div className="relative">
              <div className="p-2 rounded-lg bg-gradient-to-br from-[#FF6C6C]/15 to-pink-100 shadow-sm">
                <Shield className="w-5 h-5 text-[#FF6C6C]" />
              </div>
              {/* Decorative dot */}
              <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-gradient-to-r from-[#FF6C6C] to-pink-500 rounded-full" />
            </div>
            <div>
              <h3 className="mb-0.5 text-base font-bold text-gray-800">{role.name}</h3>
              <p className="text-xs text-gray-500">
                {role.description || 'Custom role configuration'}
              </p>
            </div>
          </div>
          <ActionDropdown
            role={role}
            activeDropdown={activeDropdown}
            setActiveDropdown={setActiveDropdown}
            onView={onView}
            onEdit={onEdit}
            onDelete={onDelete}
            teamMemberCount={teamMemberCount}
          />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="p-2 text-center bg-gradient-to-br from-blue-50 rounded-lg border border-blue-100 transition-all duration-200 to-blue-100/50 hover:from-blue-100 hover:to-blue-200/50 group-hover:shadow-sm">
            <p className="mb-0.5 text-xl font-bold text-blue-600 transition-colors group-hover:text-blue-700">{teamMemberCount}</p>
            <p className="text-xs font-medium text-blue-700/80">{teamMemberCount === 1 ? 'Member' : 'Members'}</p>
          </div>
          <div className="p-2 text-center bg-gradient-to-br from-green-50 rounded-lg border border-green-100 transition-all duration-200 to-green-100/50 hover:from-green-100 hover:to-green-200/50 group-hover:shadow-sm">
            <p className="mb-0.5 text-xl font-bold text-green-600 transition-colors group-hover:text-green-700">{moduleCount}</p>
            <p className="text-xs font-medium text-green-700/80">Modules</p>
          </div>
          <div className="p-2 text-center bg-gradient-to-br from-purple-50 rounded-lg border border-purple-100 transition-all duration-200 to-purple-100/50 hover:from-purple-100 hover:to-purple-200/50 group-hover:shadow-sm">
            <p className="mb-0.5 text-xl font-bold text-purple-600 transition-colors group-hover:text-purple-700">{totalPermissions}</p>
            <p className="text-xs font-medium text-purple-700/80">Permissions</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center pt-3 border-t border-gray-100">
          <div className="flex gap-2 items-center">
            <div className="p-1 bg-gray-100 rounded-full">
              <Users className="w-3 h-3 text-gray-500" />
            </div>
            <span className="text-xs font-medium text-gray-600">
              {teamMemberCount > 0 ? `Used by ${teamMemberCount} member${teamMemberCount !== 1 ? 's' : ''}` : 'Not used yet'}
            </span>
          </div>
          <div className="flex gap-2 items-center">
            {teamMemberCount === 0 ? (
              <span className="px-2 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full border border-green-200 shadow-sm transition-all duration-200 hover:shadow-md hover:bg-green-200">
                <CheckCircle className="inline mr-1 w-3 h-3" />
                Can Delete
              </span>
            ) : (
              <span className="px-2 py-1 text-xs font-semibold text-amber-700 bg-amber-100 rounded-full border border-amber-200 shadow-sm transition-all duration-200 hover:shadow-md hover:bg-amber-200">
                <AlertTriangle className="inline mr-1 w-3 h-3" />
                In Use
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const DeleteConfirmationModal = ({ isOpen, onClose, role, onConfirm, isDeleting }) => {
  if (!isOpen || !role) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/50"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="p-6 w-full max-w-md bg-white rounded-xl"
      >
        <div className="flex gap-3 items-center mb-4">
          <div className="p-2 bg-red-100 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">Delete Role</h3>
        </div>

        <p className="mb-6 text-gray-600">
          Are you sure you want to delete the role <strong>"{role.name}"</strong>? 
          This action cannot be undone.
        </p>

        <div className="flex gap-3 justify-end items-center">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onClose}
            disabled={isDeleting}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200 disabled:opacity-50"
          >
            Cancel
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex gap-2 items-center px-4 py-2 text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700 disabled:opacity-50"
          >
            {isDeleting ? (
              <>
                <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
                Deleting...
              </>
            ) : (
              'Delete Role'
            )}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ViewRoleModal = ({ isOpen, onClose, role }) => {
  if (!isOpen || !role) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/50"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-xl"
      >
        <div className="p-6 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <div className="flex gap-3 items-center">
              <div className="p-2 bg-gradient-to-br from-[#FF6C6C]/10 to-pink-50 rounded-lg">
                <Shield className="w-5 h-5 text-[#FF6C6C]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">{role.name}</h3>
                <p className="text-sm text-gray-500">{role.description || 'No description available'}</p>
              </div>
            </div>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="p-2 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
            >
              <XCircle className="w-5 h-5" />
            </motion.button>
          </div>
        </div>

        <div className="p-6">
          <h4 className="mb-4 font-medium text-gray-800">Permissions</h4>
          <div className="space-y-4">
            {Object.entries(role.permissions || {}).map(([module, permissions]) => (
              <div key={module} className="p-4 rounded-lg border border-gray-200">
                <h5 className="mb-2 font-medium text-gray-700">{module}</h5>
                <div className="flex flex-wrap gap-2">
                  {(permissions || []).map((permission) => (
                    <span
                      key={permission}
                      className="px-2 py-1 text-xs bg-[#FF6C6C]/10 text-[#FF6C6C] rounded-full"
                    >
                      {permission}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const ManageRoles = () => {
  const navigate = useNavigate();
  const { 
    customRoles, 
    isLoading, 
    deleteRole, 
    isDeleting,
    hasRoles 
  } = useCustomRoles();
  const { canViewRoles, canEditRoles, canDeleteRoles, canCreateRoles } = usePermissionCheck();

  const { getTeamMembersByRole, teamMembers } = useTeamManagement();

  // Debug: Log team members data once
  React.useEffect(() => {
    console.log('Team Members loaded in ManageRoles:', teamMembers);
  }, [teamMembers]);



  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, role: null });
  const [viewModal, setViewModal] = useState({ isOpen: false, role: null });

  const filteredRoles = customRoles.filter(role =>
    role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (role.description && role.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );


  const handleView = (role) => {
    setViewModal({ isOpen: true, role });
  };

  const handleEdit = (role) => {
    if (canEditRoles()) {
      navigate(`/roles/edit/${role.role_slug}`);
    } else {
      toast.error("You don't have permission to edit roles");
    }
  };

  const handleDelete = (role) => {
    if (canDeleteRoles()) {
      setDeleteModal({ isOpen: true, role });
    } else {
      toast.error("You don't have permission to delete roles");
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteModal.role) {
      try {
        await deleteRole(deleteModal.role.role_slug);
        setDeleteModal({ isOpen: false, role: null });
        toast.success('Role deleted successfully');
      } catch (error) {
        toast.error('Failed to delete role');
      }
    }
  };

  const getTeamMemberCount = (roleName) => {
    return getTeamMembersByRole(roleName).length;
  };

  // Check if user has permission to view roles
  if (!canViewRoles()) {
    return (
      <div className="bg-white">
        <Navbar page={"Manage Roles"} />
        <div className="flex justify-center items-center min-h-screen bg-gray-50">
          <div className="text-center">
            <Shield className="mx-auto mb-4 w-16 h-16 text-gray-400" />
            <h2 className="mb-2 text-xl font-semibold text-gray-800">Access Restricted</h2>
            <p className="text-gray-500">You don't have permission to manage roles.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="bg-white">
      <Navbar page={"Manage Roles"} />
      
      <div className="p-4 min-h-screen bg-gray-50 sm:p-6">
        {/* Breadcrumb */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap gap-1 items-center mb-4 text-xs text-gray-500 sm:gap-2 sm:mb-6 sm:text-sm"
        >
          <span 
            className="hover:text-[#FF6C6C] cursor-pointer transition-colors"
            onClick={() => navigate('/dashboard')}
          >
            Dashboard
          </span>
          <ChevronRight size={14} className="sm:w-4 sm:h-4" />
          <span 
            className="hover:text-[#FF6C6C] cursor-pointer transition-colors"
            onClick={() => navigate('/roles-and-permissions')}
          >
            Roles & Permissions
          </span>
          <ChevronRight size={14} className="sm:w-4 sm:h-4" />
          <span className="text-[#FF6C6C] font-medium">Manage Roles</span>
        </motion.div>

        {/* Header */}
        <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:justify-between sm:items-center sm:gap-0 sm:mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex gap-3 items-center sm:gap-4"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/roles-and-permissions')}
              className="p-2 text-gray-400 rounded-xl transition-all duration-200 hover:text-gray-600 hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </motion.button>
            <div className="flex gap-2 items-center sm:gap-3">
              <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-[#FF6C6C]/10 via-pink-50 to-white">
                <Settings className="w-5 h-5 sm:w-6 sm:h-6 text-[#FF6C6C]" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800 sm:text-2xl">Manage Roles</h1>
                <p className="text-xs text-gray-500 sm:text-sm">
                  {hasRoles ? `${customRoles.length} custom role${customRoles.length !== 1 ? 's' : ''}` : 'No roles created yet'}
                </p>
              </div>
            </div>
          </motion.div>

          {canCreateRoles() && (
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/roles/create')}
              className="w-full sm:w-auto px-4 py-2.5 text-white bg-gradient-to-r from-[#FF6C6C] to-pink-500 rounded-xl hover:from-[#FF6C6C]/90 hover:to-pink-600 transition-all duration-200 flex items-center justify-center gap-2 font-medium text-sm"
            >
              <Plus size={18} />
              Create New Role
            </motion.button>
          )}
        </div>

        {hasRoles ? (
          <>
            {/* Search and Filters */}
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search roles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-200 text-sm"
                />
              </div>
            </div>

            {/* Roles Grid */}
            {filteredRoles.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredRoles.map((role, index) => (
                  <RoleCard
                    key={`${role.id || role.role_slug || role.name || index}_${index}`}
                    role={role}
                    activeDropdown={activeDropdown}
                    setActiveDropdown={setActiveDropdown}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    teamMemberCount={getTeamMemberCount(role.name)}
                  />
                ))}
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center py-12 text-center">
                <div className="p-4 mb-4 bg-gray-100 rounded-full">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-800">No roles found</h3>
                <p className="text-gray-500">Try adjusting your search criteria</p>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col justify-center items-center py-12 text-center">
            <div className="p-4 mb-4 bg-gray-100 rounded-full">
              <Shield className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-800">No roles created yet</h3>
            <p className="mb-6 text-gray-500">Start by creating your first custom role</p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/roles/create')}
              className="px-6 py-3 text-white bg-gradient-to-r from-[#FF6C6C] to-pink-500 rounded-xl hover:from-[#FF6C6C]/90 hover:to-pink-600 transition-all duration-200 font-medium"
            >
              Create Your First Role
            </motion.button>
          </div>
        )}
      </div>

      {/* Modals */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, role: null })}
        role={deleteModal.role}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />

      <ViewRoleModal
        isOpen={viewModal.isOpen}
        onClose={() => setViewModal({ isOpen: false, role: null })}
        role={viewModal.role}
      />
    </div>
  );
};

export default ManageRoles; 